import axios from "axios";
import { authService } from "./authService";

// -------- Base URL resolution --------
function resolveBaseURL() {
  const raw = process.env.REACT_APP_SERVER_URL?.replace(/\/$/, "");
  if (!raw) return "/api/v1"; // dev proxy or same-origin prod
  if (typeof window !== "undefined" && window.location.protocol === "https:" && raw.startsWith("http://")) {
    console.warn("⚠️ Mixed content risk: HTTPS page calling HTTP API:", raw);
  }
  return `${raw}/api/v1`;
}

const api = axios.create({
  baseURL: resolveBaseURL(),
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
});

if (typeof window !== "undefined") {
  console.log("🔧 API Config", {
    baseURL: api.defaults.baseURL,
    envServer: process.env.REACT_APP_SERVER_URL || null,
    origin: window.location.origin,
    protocol: window.location.protocol,
  });
}

// -------- Request interceptor --------
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await authService.getIdToken();
      if (token) config.headers["Authorization"] = `Bearer ${token}`;
    } catch (e) {
      console.warn("(Token attach skipped)", e?.message);
    }
    if (!window.__API_REQ_COUNT) window.__API_REQ_COUNT = 0;
    if (window.__API_REQ_COUNT < 6) {
      console.log(`📡 [API ${++window.__API_REQ_COUNT}] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// -------- Response interceptor --------
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const isNetwork = !error.response;
    if (isNetwork) {
      const mixed = (typeof window !== "undefined" && window.location.protocol === "https:" && api.defaults.baseURL.startsWith("http://"));
      console.error("🌐 Network layer failure", {
        message: error.message,
        code: error.code,
        baseURL: api.defaults.baseURL,
        mixedContent: mixed,
        hints: mixed ? ["Serve API over HTTPS or same origin"] : ["Server down", "CORS/preflight blocked", "Firewall/VPN", "Ad-blocker"],
      });
      error.isConnectivityIssue = true;
      // Auto-fallback: if baseURL uses localhost but page host isn't localhost, retry once with relative path
      if (typeof window !== 'undefined' && !error.config.__triedFallback) {
        const host = window.location.hostname;
        if (host !== 'localhost' && /localhost:(\d+)/.test(api.defaults.baseURL)) {
          console.warn('⚠️ Detected localhost baseURL on non-localhost host. Retrying with relative /api/v1 ...');
          error.config.__triedFallback = true;
          const originalUrl = error.config.url; // already includes leading '/'
          const fallbackConfig = { ...error.config, baseURL: '/api/v1', url: originalUrl };
          try {
            return await axios.request(fallbackConfig);
          } catch (fallbackErr) {
            console.error('Fallback request also failed:', fallbackErr.message);
          }
        }
      }
    } else {
      const { status } = error.response;
      if (status === 401) {
        if (!error.config._isRetry) {
          try {
            const refreshed = await authService.getIdToken(true);
            if (refreshed) {
              const original = error.config;
              original._isRetry = true;
              original.headers["Authorization"] = `Bearer ${refreshed}`;
              return api.request(original);
            }
          } catch (reAuthErr) {
            console.warn("Token refresh failed", reAuthErr?.message);
          }
        }
      } else if (status >= 500) {
        console.error(`🔥 Server error ${status}`, error.response.data);
      }
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  getCurrentUser: () => api.get("/auth/me"),
  changePassword: (passwordData) =>
    api.post("/auth/change-password", passwordData),
};

// User endpoints
export const userAPI = {
  getAll: (params) => api.get("/users", { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, data) => api.put(`/users/${id}/reset-password`, data),
  resetWorkerPassword: (id) => api.post(`/users/${id}/reset-password`),
  getUsersByDepartment: (departmentId) =>
    api.get(`/users/department/${departmentId}`),
  // Role-based creation endpoints
  createAdmin: (data) => api.post("/users/admins", data),
  createFaculty: (data) => api.post("/users/faculty", data),
  createScholar: (data) => api.post("/users/scholars", data),
  createStudent: (data) => api.post("/users/students", data),
  createUser: (data) => api.post("/users/users", data),
  createWorker: (data) => api.post("/users/workers", data),
  // Bulk creation endpoints
  createBulkStudents: (data) => api.post("/users/students/bulk", data),
  createBulkScholars: (data) => api.post("/users/scholars/bulk", data),
};

// Category endpoints
export const categoryAPI = {
  getAll: (flat = false) => api.get(`/categories${flat ? "?flat=true" : ""}`),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post("/categories", data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Inventory endpoints
export const inventoryAPI = {
  getAll: () => api.get("/inventory"),
  getById: (id) => api.get(`/inventory/${id}`),
  create: (data) => api.post("/inventory", data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  updateQuantity: (id, data) => api.patch(`/inventory/${id}/quantity`, data),
  getLowStock: () => api.get("/inventory/low-stock"),
  bulkCreate: (data) => api.post("/inventory/bulk", data),
  // Adding new usage log related methods
  getItemUsageLogs: (id) => api.get(`/inventory/${id}/usage`),
  getAllUsageLogs: (params) => api.get("/inventory/logs", { params }),
  bulkCreateCsv: (formData) =>
    api.post("/inventory/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }),
  exportCsv: (params) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/inventory/export${queryString ? "?" + queryString : ""}`;
    window.open(url, "_blank");
  },
};

// Lab endpoints
export const labAPI = {
  getAll: () => api.get("/labs"),
  getById: (id) => api.get(`/labs/${id}`),
  create: (data) => api.post("/labs", data),
  update: (id, data) => api.put(`/labs/${id}`, data),
  delete: (id) => api.delete(`/labs/${id}`),
};

// Booking endpoints
export const bookingAPI = {
  // Fetch bookings with optional query parameters
  getAll: (params) => api.get("/bookings", { params }),
  getPending: () => api.get("/bookings/pending"),
  create: (data) => api.post("/bookings", data),
  updateStatus: (id, statusData) =>
    api.patch(`/bookings/${id}/status`, statusData),
  getLogs: (params) => api.get("/bookings/logs", { params }),
};
// Registration token endpoints for student self-signup
export const tokenAPI = {
  // Get all tokens generated by current user (faculty)
  getAll: () => api.get("/registration-tokens"),
  // Generate a new registration token
  create: () => api.post("/registration-tokens"),
};

// System management endpoints (Developer Master)
export const systemAPI = {
  // System configuration
  getSystemConfig: () => api.get("/system/config"),
  updateSystemConfig: (configData) => api.put("/system/config", configData),
  getSystemStatus: () => api.get("/system/status"),

  // Department management
  getDepartments: () => api.get("/system/departments"),
  createDepartment: (departmentData) =>
    api.post("/system/departments", departmentData),
  updateDepartment: (id, departmentData) =>
    api.put(`/system/departments/${id}`, departmentData),
  deleteDepartment: (id) => api.delete(`/system/departments/${id}`),

  // System settings
  getSystemSettings: () => api.get("/system/settings"),
  updateSystemSettings: (settingsData) =>
    api.put("/system/settings", settingsData),

  // Predefined data
  getPredefinedCategories: () => api.get("/system/predefined/categories"),
  updatePredefinedCategories: (categoriesData) =>
    api.put("/system/predefined/categories", categoriesData),
  getPredefinedUnits: () => api.get("/system/predefined/units"),
  updatePredefinedUnits: (unitsData) =>
    api.put("/system/predefined/units", unitsData),

  // System maintenance
  getSystemLogs: (params) => api.get("/system/logs", { params }),
  clearSystemCache: () => api.post("/system/cache/clear"),
  // System Activities Route
  getRecentActivities: () => api.get("/system/activities"),
};

// Maintenance request endpoints
export const maintenanceAPI = {
  // Create a new maintenance request
  createRequest: (data) => api.post("/maintenance/requests", data),

  // Get user's own maintenance requests
  getMyRequests: () => api.get("/maintenance/requests/my-requests"),

  // Get all maintenance requests (Admin/Worker view)
  getAllRequests: (params) => api.get("/maintenance/requests", { params }),

  // Get worker's assigned requests
  getAssignedRequests: () => api.get("/maintenance/requests/assigned"),

  // Get request by ID
  getRequestById: (id) => api.get(`/maintenance/requests/${id}`),

  // Update request (general function for multiple fields)
  updateRequest: (id, data) => api.put(`/maintenance/requests/${id}`, data),

  // Admin operations
  assignWorker: (id, data) => api.patch(`/maintenance/requests/${id}/assign`, data),

  // Worker operations
  updateStatus: (id, data) => api.patch(`/maintenance/requests/${id}/status`, data),

  // User operations
  closeRequest: (id, data) => api.patch(`/maintenance/requests/${id}/close`, data),

  // Statistics
  getStats: () => api.get("/maintenance/stats"),

  // Delete request (Admin only)
  deleteRequest: (id) => api.delete(`/maintenance/requests/${id}`),
};

// Worker management endpoints
export const workerAPI = {
  // Create a new worker
  createWorker: (data) => api.post("/workers", data),

  // Get all workers
  getAllWorkers: (params) => api.get("/workers", { params }),

  // Get available workers for assignment
  getAvailableWorkers: (params) => api.get("/workers/available", { params }),

  // Get worker by ID
  getWorkerById: (id) => api.get(`/workers/${id}`),

  // Update worker
  updateWorker: (id, data) => api.put(`/workers/${id}`, data),

  // Update worker availability
  updateAvailability: (id, data) => api.patch(`/workers/${id}/availability`, data),

  // Get worker statistics
  getWorkerStats: (id) => api.get(`/workers/${id}/stats`),

  // Delete worker
  deleteWorker: (id) => api.delete(`/workers/${id}`),
};

// HOD (Head of Department) management endpoints
export const hodAPI = {
  // Create a new HOD
  createHOD: (data) => api.post("/hods", data),

  // Get all HODs
  getAllHODs: (params) => api.get("/hods", { params }),

  // Get HOD by ID
  getHODById: (id) => api.get(`/hods/${id}`),

  // Get HOD by category
  getHODByCategory: (category) => api.get(`/hods/category/${category}`),

  // Get current HOD profile (for authenticated HOD user)
  getMyProfile: () => api.get("/hods/me"),

  // Update HOD profile
  updateHOD: (id, data) => api.put(`/hods/${id}`, data),

  // Get pending requests for HOD review
  getPendingRequests: () => api.get("/hods/requests/pending"),

  // Get assigned requests for HOD
  getAssignedRequests: () => api.get("/hods/requests/assigned"),

  // Get all requests for HOD's category
  getAllCategoryRequests: () => api.get("/hods/requests/all"),

  // Review maintenance request (approve/reject)
  reviewRequest: (requestId, data) => api.patch(`/hods/requests/${requestId}/review`, data),

  // Assign worker to approved request
  assignWorker: (requestId, data) => api.patch(`/hods/requests/${requestId}/assign`, data),

  // Assign external/temporary worker to approved request
  assignExternalWorker: (requestId, data) => api.patch(`/hods/requests/${requestId}/assign-external`, data),

  // Get HOD dashboard statistics
  getHODStats: () => api.get("/hods/me/dashboard"),

  // Get workers under HOD supervision (requires HOD ID)
  getManagedWorkers: (hodId) => api.get(`/hods/${hodId}/workers`),

  // Delete worker under HOD supervision
  deleteWorker: (workerId) => api.delete(`/hods/workers/${workerId}`),

  // Update HOD availability/settings
  updateSettings: (data) => api.patch("/hods/settings", data),

  // Delete HOD
  deleteHOD: (id) => api.delete(`/hods/${id}`),
};

export default api;
